/**
 * Task vertical configurations
 *
 * Each vertical defines a predefined workspace/view for specific teams or workflows.
 * Verticals can define base filters that are applied by default, and each vertical
 * can have its own custom view component.
 *
 * Architecture:
 * - Verticals are selected via URL param: /tasks?vertical=claims
 * - Base filters are merged with user-applied filters
 * - New verticals can be added without touching routing code
 */

import { FilterValue } from '@/services/types/pagination-types';
import {
  CanonicalTaskGroup,
  TaskExternalCMSUpdateStatus,
} from '@/services/types/task-types';

import { MetricDefinition } from './metricsConfig';

export interface VerticalConfig {
  /** Unique identifier for the vertical */
  id: string;
  /** Display label shown in the dropdown */
  label: string;
  /** Base filters applied for this vertical */
  baseFilters: Record<string, FilterValue>;
  /** Description/tooltip for the vertical */
  description?: string;
  /**
   * Custom metrics to display for this vertical.
   * If not provided, DEFAULT_TASK_METRICS from metricsConfig.ts is used.
   *
   * @example
   * ```typescript
   * metrics: [
   *   { id: 'total', label: 'Total', ... },
   *   { id: 'urgent', label: 'Urgent', backendCategory: 'active', ... },
   * ]
   * ```
   */
  metrics?: MetricDefinition[];
}

/**
 * Available task verticals
 *
 * Note: The 'all' vertical shows all tasks with no base filters.
 * Other verticals can filter by team tags, canonical groups, assignee roles,
 * or any combination of filters supported by the backend.
 */
export const TASK_VERTICALS: Record<string, VerticalConfig> = {
  all: {
    id: 'all',
    label: 'All Tasks',
    baseFilters: {
      include_all_canonical: true,
      exclude_hidden_matter_tasks: true,
    },
    description: 'All tasks across all teams and workflows',
  },
  cms_updates: {
    id: 'cms_updates',
    label: 'Needs CMS Update',
    baseFilters: {
      include_all_canonical: true,
      exclude_hidden_matter_tasks: true,
      external_cms_update_status: TaskExternalCMSUpdateStatus.NOT_UPDATED,
    },
    description: 'Tasks that need to be updated in the external CMS',
  },
  claims: {
    id: 'claims',
    label: 'Claims',
    baseFilters: {
      // Include all task types under 1P Insurance and 3P Insurance groups
      // This covers: 1P insurance claims and 3P insurance claims
      canonical_groups: [
        CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
        CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
      ],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks managed by the Claims team',
  },
  liability: {
    id: 'liability',
    label: 'Liability',
    baseFilters: {
      // Include all task types under Police Report & Investigation group
      // This covers: Police Report Escalation, Freedom of Information Act, Incident Report
      canonical_groups: [CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks related to police reports and investigation',
  },
  medical: {
    id: 'medical',
    label: 'Medical',
    baseFilters: {
      // Include all task types under Medical task groups
      // This covers: Medical Records, Treatment
      canonical_groups: [
        CanonicalTaskGroup.MEDICAL_RECORDS,
        CanonicalTaskGroup.TREATMENT,
      ],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks related to medical records and treatment',
  },
  demand: {
    id: 'demand',
    label: 'Demand',
    baseFilters: {
      // Include all task types under Demand Letter group
      canonical_groups: [CanonicalTaskGroup.DEMAND_LETTER],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks related to demand letters',
  },
  intake: {
    id: 'intake',
    label: 'Initial Review',
    baseFilters: {
      // Include all task types under Initial Review group
      canonical_groups: [CanonicalTaskGroup.INITIAL_REVIEW],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks related to initial review',
  },
  intakes: {
    id: 'intakes',
    label: 'Intake',
    baseFilters: {
      // Include all task types under Intake group
      canonical_groups: [CanonicalTaskGroup.INTAKE],
      exclude_hidden_matter_tasks: true,
    },
    description: 'Tasks related to intake',
  },
  miscellaneous: {
    id: 'miscellaneous',
    label: 'Miscellaneous',
    baseFilters: {
      // Include all task types under Miscellaneous and Liens groups
      canonical_groups: [
        CanonicalTaskGroup.MISCELLANEOUS,
        CanonicalTaskGroup.LIENS,
      ],
      exclude_hidden_matter_tasks: true,
    },
    description:
      "Miscellaneous tasks and liens that don't fit other categories",
  },
  external: {
    id: 'external',
    label: 'External Tasks',
    baseFilters: {},
    description: 'Tasks from external systems (e.g., Filevine)',
  },
} as const;

export type VerticalId = keyof typeof TASK_VERTICALS;

/**
 * Get vertical config by ID, with fallback to 'all'
 */
export function getVerticalConfig(verticalId: string): VerticalConfig {
  return TASK_VERTICALS[verticalId as VerticalId] || TASK_VERTICALS.all;
}

/**
 * Get list of all vertical options for dropdown
 * Returns verticals with 'all' first (default), other verticals alphabetically,
 * 'cms_updates' above 'external', and 'external' last
 */
export function getVerticalOptions() {
  const verticals = Object.values(TASK_VERTICALS);

  // Sort: 'all' first, then other verticals alphabetically,
  // then cms_updates, then 'external' last
  return verticals.sort((a, b) => {
    // 'all' always first
    if (a.id === 'all') return -1;
    if (b.id === 'all') return 1;
    // 'external' always last
    if (a.id === 'external') return 1;
    if (b.id === 'external') return -1;
    // 'cms_updates' right above external (second from bottom)
    if (a.id === 'cms_updates') return 1;
    if (b.id === 'cms_updates') return -1;
    // Others alphabetically
    return a.label.localeCompare(b.label);
  });
}

// ----------------------------------------------------------------------------
// UI Display Config (Grouping & Ordering)
// ----------------------------------------------------------------------------

// Group display order and custom labels for cleaner UI
// Used by Advanced Filters -> TaskTypeFilter and TaskCanonicalGroupPills
export const GROUP_DISPLAY_ORDER = [
  CanonicalTaskGroup.INTAKE,
  CanonicalTaskGroup.INITIAL_REVIEW,
  CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION,
  CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
  CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
  CanonicalTaskGroup.LIENS,
  CanonicalTaskGroup.TREATMENT,
  CanonicalTaskGroup.MEDICAL_RECORDS,
  CanonicalTaskGroup.DEMAND_LETTER,
  CanonicalTaskGroup.MISCELLANEOUS,
  CanonicalTaskGroup.ADDITIONAL_INSURANCE,
];

// Simplified display names for groups
export const GROUP_DISPLAY_NAMES: Partial<Record<CanonicalTaskGroup, string>> =
  {
    // INITIAL_REVIEW doesn't need an override - canonical name matches display name
    [CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION]: 'Liability',
    [CanonicalTaskGroup.FIRST_PARTY_INSURANCE]: '1P Insurance',
    [CanonicalTaskGroup.THIRD_PARTY_INSURANCE]: '3P Insurance',
    [CanonicalTaskGroup.DEMAND_LETTER]: 'Demands',
  };
